import { dateLabel } from './format';
import type { ClosePeriodInput, PeriodClosingGateway, PeriodClosure } from './periodClosing';

/**
 * In-memory Periodenabschluss store, mirroring the other demo gateways: state lives for the
 * session only. A real implementation persists closures (they are the record of what was handed
 * to the Fibu and when), which is why the gateway interface is kept async from the start.
 */
export class DemoPeriodClosingGateway implements PeriodClosingGateway {
  private closures: PeriodClosure[] = [];
  private sequence = 0;

  async listClosures(tenantId: string): Promise<readonly PeriodClosure[]> {
    return this.closures
      .filter((closure) => closure.tenantId === tenantId)
      .sort((left, right) => right.from.localeCompare(left.from));
  }

  async closePeriod(input: ClosePeriodInput): Promise<PeriodClosure> {
    const { from, to } = input.period;
    if (!from || !to) {
      throw new Error('Für einen Abschluss müssen Beginn und Ende des Zeitraums gesetzt sein.');
    }
    if (from > to) {
      throw new Error('Das Ende des Zeitraums liegt vor dem Beginn.');
    }
    const overlapping = this.closures.find(
      (closure) => closure.tenantId === input.tenantId && closure.from <= to && closure.to >= from,
    );
    if (overlapping) {
      throw new Error(`Der Zeitraum überschneidet sich mit einem bestehenden Abschluss (${dateLabel(overlapping.from)} – ${dateLabel(overlapping.to)}).`);
    }

    this.sequence += 1;
    const closure: PeriodClosure = {
      id: `closure-${input.tenantId}-${this.sequence}`,
      tenantId: input.tenantId,
      from,
      to,
      closedAt: new Date().toISOString(),
      closedBy: input.closedBy,
      note: input.note?.trim() ?? '',
      snapshot: input.snapshot,
    };
    this.closures = [...this.closures, closure];
    return closure;
  }

  async reopenPeriod(tenantId: string, closureId: string): Promise<void> {
    const exists = this.closures.some((closure) => closure.id === closureId && closure.tenantId === tenantId);
    if (!exists) {
      throw new Error('Der Abschluss wurde nicht gefunden.');
    }
    this.closures = this.closures.filter((closure) => closure.id !== closureId);
  }
}

export function createDemoPeriodClosingGateway(): PeriodClosingGateway {
  return new DemoPeriodClosingGateway();
}
